import React, { forwardRef, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionMeta, MenuPlacement } from 'react-select';

import { ISelect, Select } from 'common/components/form/Select/Select';
import { useFormikField } from 'common/components/form/useFormikField';
import { FieldWrapper } from 'common/components/form/Wrapper';
import { ISelectModel } from 'common/models';
import { debounce } from 'helper/debounce';
import { useScreenSizeDetection } from 'common/hooks';

interface ISelectField extends Omit<ISelect, 'onChange' | 'defaultValue' | 'hasError' | 'options'> {
  label?: string;
  required?: boolean;
  options?: ISelectModel<any>[];
  useLabelAsValue?: boolean;
  margin?: string;
  customError?: boolean;
  customErrorText?: string;
  enableDefaultClearOption?: boolean;
  overflowMenuPortalTarget?: boolean;
  onChange?: (
    selectedValue: ISelectModel<string | number>,
    additionalActions: ActionMeta<any>,
  ) => void;
  ref?: any;
  enableAsyncSelect?: boolean;
  onInputChange?: (newValue: any) => void;
  selectDebounceTime?: number;
  enableSearch?: boolean;
  menuIsOpen?: boolean;
  autoFocus?: boolean;
  disableDropdownArrow?: boolean;
  disableMultiSelection?: boolean;
  tooltip?: ReactNode;
  dropdownMenuHeight?: string;
  showReactWindowList?: boolean;
  menuPlacement?: MenuPlacement;
  justifyContentLabel?: string;
  isMultiSelect?: boolean;
  labelStyle?: React.CSSProperties;
  extendStyles?: {
    valueContainer?: React.CSSProperties;
    menuList?: React.CSSProperties;
    menu?: React.CSSProperties;
    menuPortal?: React.CSSProperties;
    control?: React.CSSProperties;
    placeholder?: React.CSSProperties;
    option?: (styles: any, additionalProps: any) => React.CSSProperties;
    singleValue?: React.CSSProperties;
  };
  onMenuOpen?: () => void;
  createElementIfNotExistInDropdown?: boolean; // to work include "enableSearch"
  placeholderColor?: string;
  containerStyles?: string;
  enableSingleValueTooltip?: boolean;
  keepSearchOnSelect?: boolean;
  formatOptionLabel?: (option: any, meta: any) => ReactNode;
  searchFromStart?: boolean;
}

export const SelectField = forwardRef(
  (
    {
      label,
      name,
      options = [],
      required = false,
      disabled = false,
      clearable = false,
      isLoading = false,
      isMultiSelect = false,
      isFilterDropdown = false,
      useLabelAsValue = false,
      margin,
      customError,
      customErrorText,
      enableDefaultClearOption,
      overflowMenuPortalTarget,
      onChange,
      onInputChange,
      selectDebounceTime,
      enableSearch,
      menuIsOpen,
      autoFocus,
      disableDropdownArrow,
      disableMultiSelection,
      tooltip,
      dropdownMenuHeight,
      showReactWindowList,
      menuPlacement,
      justifyContentLabel,
      placeholder,
      extendStyles,
      onMenuOpen,
      labelStyle,
      createElementIfNotExistInDropdown,
      placeholderColor,
      containerStyles,
      enableSingleValueTooltip = false,
      keepSearchOnSelect = false,
      formatOptionLabel,
      searchFromStart,
    }: ISelectField,

    ref,
  ) => {
    const { t } = useTranslation();

    const { isMobile } = useScreenSizeDetection();
    console.log();

    const [elementNameIfNotExistInDropdown, setElementNameIfNotExistInDropdown] = useState('');
    const [createOptionValue, setCreateOptionValue] = useState<{ value: string; label: string }[]>(
      [],
    );
    const [createOptions, setCreateOptions] = useState(options);

    const handleCreate = (inputValue: any) => {
      const newOption = {
        label: inputValue,
        value: inputValue,
      };

      setCreateOptions(prev => [...prev, newOption]);
      setCreateOptionValue(prev => [...prev, newOption]);
    };

    const { value, error, hasError, setValue, validate } = useFormikField(name);

    const defaultClearOption = { label: t('Select...'), value: -1 };
    const optionValues = enableDefaultClearOption ? [defaultClearOption, ...options] : options;

    const selectedValues = Array.isArray(value)
      ? optionValues.filter(x => value.includes(x.value))
      : optionValues.find(x => x.value === value);

    let _placeholder = placeholder ? placeholder : '';
    if (isMultiSelect) {
      if (Array.isArray(value)) {
        const singleSelected =
          value.length === 1
            ? typeof value[0] === 'object'
              ? value[0]
              : optionValues.find(x => x.value === value[0])
            : undefined;

        _placeholder =
          createElementIfNotExistInDropdown && placeholder && value.length === 0
            ? placeholder
            : value.length === 0
            ? ''
            : value.length === 1
            ? singleSelected?.label ?? t('One chosen')
            : value.length > 1 && value.length < 5
            ? 'Two-Four selected'
            : 'More selected';
      }
    }

    const request = debounce((asyncValue: string) => {
      return onInputChange && onInputChange(asyncValue);
    }, selectDebounceTime || 500);

    return (
      <FieldWrapper
        name={name}
        label={label}
        isRequired={required}
        isDisabled={disabled}
        error={customErrorText ?? error}
        margin={margin}
        tooltip={tooltip}
        justifyContentLabel={justifyContentLabel}
        labelStyle={labelStyle}
        placeholderColor={placeholderColor}
      >
        <Select
          containerStyles={containerStyles}
          ref={ref as any}
          autoFocus={autoFocus}
          disableDropdownArrow={disableDropdownArrow}
          placeholder={_placeholder}
          name={name}
          menuIsOpen={menuIsOpen}
          defaultValue={!createElementIfNotExistInDropdown ? selectedValues : createOptionValue}
          isMultiSelectDataLength={isMultiSelect && value?.length > 0 ? value?.length : ''}
          overflowMenuPortalTarget={overflowMenuPortalTarget}
          hasError={customError ?? hasError}
          disabled={disabled}
          clearable={clearable}
          isLoading={isLoading}
          isMultiSelect={isMultiSelect}
          options={!createElementIfNotExistInDropdown ? optionValues : createOptions}
          isFilterDropdown={isFilterDropdown}
          onMenuOpen={onMenuOpen}
          onChange={(selected: any, additionalActions: ActionMeta<any>) => {
            onChange && onChange(selected, additionalActions);

            if (createElementIfNotExistInDropdown) {
              setCreateOptionValue(selected);
              setValue(selected);

              return;
            }

            if (isMultiSelect && !disableMultiSelection) {
              if (Array.isArray(selected)) {
                setValue(selected);
              }

              return;
            }

            if (!selected) {
              setValue(null);
            } else if (isMultiSelect) {
              setValue(selected.map((x: any) => x.value));
            } else if (useLabelAsValue) {
              setValue(selected.label);
            } else if (enableDefaultClearOption && selected.value < 0) {
              setValue('');
            } else {
              setValue(selected.value);
            }
          }}
          enableSearch={enableSearch}
          onInputChange={(inputValue: string) => {
            if (createElementIfNotExistInDropdown) {
              setElementNameIfNotExistInDropdown(inputValue);
            }

            request(inputValue);
          }}
          onBlur={validate}
          disableMultiSelection={disableMultiSelection}
          dropdownMenuHeight={dropdownMenuHeight}
          showReactWindowList={showReactWindowList}
          menuPlacement={menuPlacement}
          extendStyles={extendStyles}
          onCreateOption={handleCreate}
          createElementIfNotExistInDropdown={createElementIfNotExistInDropdown}
          elementNameIfNotExistInDropdown={elementNameIfNotExistInDropdown}
          enableSingleValueTooltip={enableSingleValueTooltip}
          keepSearchOnSelect={keepSearchOnSelect}
          formatOptionLabel={formatOptionLabel}
          searchFromStart={searchFromStart}
        />
      </FieldWrapper>
    );
  },
);

SelectField.displayName = 'SelectField';
